import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { ParameterApi } from '@app/api';
import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { RemoveButton } from '@app/components/RemoveButton';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { useParameters } from '@app/hooks/useParameters';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import {
  PARAMETER_SOURCE,
  Parameter,
  isOverridden,
  valueAfterReset
} from '@app/model/Parameter';
import { useParameterStore } from '@app/store/parameterStore';
import { toApiError } from '@app/utils/ApiError';
import { hasRealmRole } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';
import {
  ActionList,
  ActionListItem,
  Alert,
  AlertVariant,
  Bullseye,
  Button,
  Card,
  CardBody,
  Checkbox,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Pagination,
  PaginationVariant,
  SearchInput,
  Split,
  SplitItem,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip
} from '@patternfly/react-core';
import { ExclamationTriangleIcon, HistoryIcon, LockIcon, RedoAltIcon, SearchIcon } from '@patternfly/react-icons';
import { ExpandableRowContent, InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

import { ParameterHistoryModal } from './ParameterHistoryModal';
import { ParameterValueEditor } from './ParameterValueEditor';

/** How each source reads in the table, and how loudly it says "somebody changed this". */
const SOURCE_LABELS: Record<PARAMETER_SOURCE, { text: string; color: 'blue' | 'teal' | 'grey'; hint: string }> = {
  [PARAMETER_SOURCE.DATABASE]: {
    text: 'Overridden',
    color: 'blue',
    hint: 'Set here by an administrator. This is the only case a reset has anything to drop.'
  },
  [PARAMETER_SOURCE.PROPERTIES]: {
    text: 'Configured',
    color: 'teal',
    hint: 'Coming from the deployment: the properties file, a system property or an environment variable.'
  },
  [PARAMETER_SOURCE.DEFAULT]: {
    text: 'Default',
    color: 'grey',
    hint: 'Nothing is configured anywhere, so the value declared in the code is in force.'
  }
};

/**
 * The settings an administrator may change while the service is running, at /parameters.
 *
 * Dynamic on purpose: every row, its widget and its validation come from the catalog the service
 * returns, so a parameter added on that side appears here and is editable with no change to this
 * page. Nothing about any individual setting is named in this file.
 *
 * Reads through useParameterStore, the same cache the rest of the app reads a setting from, but
 * forced past it on mount — this page has to show what the service is using right now. Sharing the
 * cache rather than fetching privately is what makes a change here land on the other readers in
 * this tab immediately, without waiting for the websocket to come back around.
 *
 * Not an entity store, unlike the other administration pages: the list is the service's catalog
 * rather than a table, so a parameter nobody has overridden has no row and therefore no id.
 */
const GeneralSettings: React.FunctionComponent = () => {
  const { addNotification } = useNotification();

  // The crumb is otherwise title-cased from the URL segment, which reads "General" while the
  // menu item and the heading both say General Settings.
  useBreadcrumbLabel('General Settings');

  /**
   * Read through the shared cache, forced: this is the one page that must show what the service is
   * using right now rather than what the cache last heard. Sharing it with the rest of the app is
   * what makes a write here visible to a consumer immediately, without waiting for the websocket
   * to come back around to this tab.
   */
  const { parameters, error, retry } = useParameters(true);
  const applyWrite = useParameterStore(state => state.applyWrite);

  // Writing needs the admin role — every method on /parameters other than the two reads is
  // admin-only. Reading is open to any authenticated user, so this page is legible to everyone and
  // editable by admins, rather than being hidden from the people who may look but not touch.
  // Only decides what to offer; the service enforces it regardless.
  const isAdmin = hasRealmRole(ROLE.ADMIN);

  const [orphans, setOrphans] = React.useState<string[]>([]);

  // --- UI state ---
  const [filterText, setFilterText] = React.useState('');
  const [overriddenOnly, setOverriddenOnly] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const { perPage, setPerPage } = useTablePreferences('parameters', 20);
  const [expandedNames, setExpandedNames] = React.useState<Set<string>>(new Set());
  const [savingName, setSavingName] = React.useState<string | null>(null);
  const [historyOf, setHistoryOf] = React.useState<Parameter | null>(null);
  const [resetting, setResetting] = React.useState<Parameter | null>(null);

  /**
   * Leftover values, alongside the settings but never able to fail the page: a service too old to
   * have the endpoint answers 404, which is not a reason to stop showing the settings.
   *
   * Not asked for at all unless the user could act on the answer. The endpoint is admin-only, so
   * for everybody else this would be a guaranteed 403 — caught here, but still logged by the
   * browser itself, which puts an error in the console of every non-admin who opens the page.
   */
  const loadOrphans = React.useCallback(async () => {
    if (!isAdmin) {
      setOrphans([]);
      return;
    }
    try {
      setOrphans(await ParameterApi.orphans());
    } catch {
      setOrphans([]);
    }
  }, [isAdmin]);

  const load = React.useCallback(async () => {
    await Promise.all([retry(), loadOrphans()]);
  }, [retry, loadOrphans]);

  React.useEffect(() => {
    beginLoading();
    void loadOrphans().finally(endLoading);
  }, [loadOrphans]);

  const onSaveValue = async (parameter: Parameter, value: string) => {
    setSavingName(parameter.name);
    try {
      applyWrite(await ParameterApi.set(parameter.name, value));
      addNotification(
        'Setting updated',
        AlertVariant.success,
        `${parameter.property} is now ${value}. It takes effect immediately, and within a minute on every other replica.`
      );
    } catch (err) {
      addNotification(`Error updating ${parameter.property}`, AlertVariant.danger, toApiError(err).message);
    } finally {
      setSavingName(null);
    }
  };

  const onResetConfirm = async () => {
    const parameter = resetting;
    setResetting(null);
    if (!parameter) return;

    setSavingName(parameter.name);
    try {
      const reset = await ParameterApi.reset(parameter.name);
      applyWrite(reset);
      addNotification(
        'Setting reset',
        AlertVariant.info,
        `${parameter.property} is back to ${reset.value}, from ${SOURCE_LABELS[reset.source].text.toLowerCase()}.`
      );
    } catch (err) {
      addNotification(`Error resetting ${parameter.property}`, AlertVariant.danger, toApiError(err).message);
    } finally {
      setSavingName(null);
    }
  };

  const onRemoveOrphan = async (key: string) => {
    beginLoading();
    try {
      await ParameterApi.removeOrphan(key);
      setOrphans(current => current.filter(orphan => orphan !== key));
      addNotification('Orphan removed', AlertVariant.info, `The leftover row for ${key} has been deleted.`);
    } catch (err) {
      addNotification(`Error removing ${key}`, AlertVariant.danger, toApiError(err).message);
    } finally {
      endLoading();
    }
  };

  const toggleExpanded = (name: string) => {
    setExpandedNames(current => {
      const next = new Set(current);
      if (!next.delete(name)) next.add(name);
      return next;
    });
  };

  // --- Filtering & pagination ---
  const filteredRows = React.useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    return parameters.filter(parameter => {
      if (overriddenOnly && !isOverridden(parameter)) return false;
      if (!needle) return true;
      return (
        parameter.property.toLowerCase().includes(needle) ||
        parameter.name.toLowerCase().includes(needle) ||
        (parameter.description ?? '').toLowerCase().includes(needle) ||
        (parameter.value ?? '').toLowerCase().includes(needle)
      );
    });
  }, [parameters, filterText, overriddenOnly]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  const overriddenCount = React.useMemo(() => parameters.filter(isOverridden).length, [parameters]);

  /** The load was refused rather than failed: an authenticated user without the admin role. */
  const isForbidden = error?.code === 403;

  const buildPagination = (variant: 'bottom' | 'top') => (
    <Pagination
      isCompact
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant}
      isStatic
    />
  );

  const clearFilters = () => {
    setFilterText('');
    setOverriddenOnly(false);
    setPage(1);
  };

  return (
    <React.Fragment>
      {/* Header */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Split hasGutter>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">General Settings</Title>
          </SplitItem>
          <SplitItem isFilled />
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <PageActionsMenu onRefresh={load} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
        <Content component="p" style={{ marginTop: 'var(--pf-t--global--spacer--sm)' }}>
          Settings the service reads while it runs, so changing one here takes effect without a
          redeploy. A change is live immediately, and within a minute on every other replica.
          Every change is recorded against the user who made it.
        </Content>
        {!isAdmin && (
          <Alert
            isInline
            variant="info"
            title="You are viewing these settings"
            style={{ marginTop: 'var(--pf-t--global--spacer--md)' }}
          >
            Changing one needs the administrator role. Everything here is readable because several
            of these settings decide how this application behaves.
          </Alert>
        )}
      </PageSection>

      {/* Settings table */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <CardBody>
            <Toolbar id="parameters-toolbar" clearAllFilters={clearFilters}>
              <ToolbarContent>
                <ToolbarItem>
                  <SearchInput
                    aria-label="Search settings"
                    placeholder="Search by name or description..."
                    value={filterText}
                    onChange={(_e, val) => { setFilterText(val); setPage(1); }}
                    onClear={() => { setFilterText(''); setPage(1); }}
                  />
                </ToolbarItem>
                <ToolbarItem>
                  <Checkbox
                    id="parameters-overridden-only"
                    label={`Overridden only (${overriddenCount})`}
                    isChecked={overriddenOnly}
                    onChange={(_e, checked) => { setOverriddenOnly(checked); setPage(1); }}
                  />
                </ToolbarItem>
                <ToolbarItem variant="pagination">{buildPagination('top')}</ToolbarItem>
              </ToolbarContent>
            </Toolbar>

            {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
                gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
            <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset-lg))', minHeight: 0 }}>
              <InnerScrollContainer>
                <Table aria-label="Runtime settings" isStickyHeader variant="compact" gridBreakPoint="">
                  <Thead>
                    <Tr>
                      <Th screenReaderText="Row expansion" />
                      <Th width={40}>Setting</Th>
                      <Th width={30}>Value</Th>
                      <Th width={15}>Source</Th>
                      <Th width={10} isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                    </Tr>
                  </Thead>

                  {paginatedRows.map((row, rowIndex) => {
                    const isExpanded = expandedNames.has(row.name);
                    const source = SOURCE_LABELS[row.source];
                    const isSaving = savingName === row.name;

                    return (
                      <Tbody key={row.name} isExpanded={isExpanded}>
                        <Tr>
                          <Td
                            expand={{
                              rowIndex,
                              isExpanded,
                              onToggle: () => toggleExpanded(row.name),
                              expandId: `parameter-${row.name}`
                            }}
                          />
                          <Td dataLabel="Setting" modifier="breakWord">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <b>{row.property}</b>
                              <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>
                                {row.description}
                              </span>
                              {row.error && (
                                <Label isCompact color="red" icon={<ExclamationTriangleIcon />}>
                                  {row.error}
                                </Label>
                              )}
                            </div>
                          </Td>
                          <Td dataLabel="Value" modifier="breakWord">
                            <ParameterValueEditor
                              parameter={row}
                              isSaving={isSaving}
                              isReadOnly={!isAdmin}
                              onSave={value => onSaveValue(row, value)}
                            />
                          </Td>
                          <Td dataLabel="Source">
                            <Tooltip
                              content={
                                row.source === PARAMETER_SOURCE.PROPERTIES && row.configSourceName
                                  ? `${source.hint} Read from ${row.configSourceName}.`
                                  : source.hint
                              }
                            >
                              <Label isCompact color={source.color}>{source.text}</Label>
                            </Tooltip>
                          </Td>
                          {/* Both actions are admin-only on the service — reset writes, and the
                              history carries the names of the people who made each change — so for
                              everyone else the column is left out rather than shown leading nowhere. */}
                          <Td dataLabel="Actions" modifier="nowrap"
                            isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>
                            {isAdmin && (
                              <ActionList>
                                <ActionListItem>
                                  <Tooltip
                                    content={
                                      isOverridden(row)
                                        ? `Drop the override and go back to ${valueAfterReset(row)}`
                                        : 'Nothing to reset — this setting has no override'
                                    }
                                  >
                                    <Button
                                      variant="plain"
                                      aria-label={`Reset ${row.property}`}
                                      icon={<Icon size="lg"><RedoAltIcon /></Icon>}
                                      isDisabled={!isOverridden(row) || isSaving}
                                      onClick={() => setResetting(row)}
                                    />
                                  </Tooltip>
                                </ActionListItem>
                                <ActionListItem>
                                  <Tooltip content="Change history">
                                    <Button
                                      variant="plain"
                                      aria-label={`History of ${row.property}`}
                                      icon={<Icon size="lg"><HistoryIcon /></Icon>}
                                      onClick={() => setHistoryOf(row)}
                                    />
                                  </Tooltip>
                                </ActionListItem>
                              </ActionList>
                            )}
                          </Td>
                        </Tr>

                        {isExpanded && (
                          <Tr isExpanded>
                            <Td />
                            <Td colSpan={4}>
                              <ExpandableRowContent>
                                <ParameterDetail parameter={row} />
                              </ExpandableRowContent>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    );
                  })}

                  <Tbody>
                    {/* Reading is open to any authenticated user on a current service, so this is
                        reached only against one old enough to have had the whole endpoint behind the
                        admin role. Worth keeping and worth being specific about: a 403 comes back
                        with an empty body, so the generic failure state would say "an unknown error
                        occurred", which tells the user nothing they can act on. */}
                    {isForbidden && (
                      <Tr>
                        <Td colSpan={5}>
                          <Bullseye>
                            <EmptyState
                              status="warning"
                              headingLevel="h4"
                              icon={LockIcon}
                              titleText="You cannot view these settings"
                            >
                              <EmptyStateBody>
                                This service keeps its runtime settings behind the administrator role.
                                Ask an administrator to grant it, or to make the change for you.
                              </EmptyStateBody>
                            </EmptyState>
                          </Bullseye>
                        </Td>
                      </Tr>
                    )}
                    {/* Nothing to show: "the load failed" and "nothing matches" are different answers */}
                    {error && !isForbidden && filteredRows.length === 0 && (
                      <DataLoadErrorRow colSpan={5} error={error} entityLabel="the settings" onRetry={load} />
                    )}
                    {!error && filteredRows.length === 0 && (
                      <Tr>
                        <Td colSpan={5}>
                          <Bullseye>
                            <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No settings found">
                              <EmptyStateBody>
                                {parameters.length === 0
                                  ? 'The service reported no configurable settings.'
                                  : 'No setting matches the current filters.'}
                              </EmptyStateBody>
                              {parameters.length > 0 && (
                                <EmptyStateFooter>
                                  <EmptyStateActions>
                                    <Button variant="link" onClick={clearFilters}>Clear filters</Button>
                                  </EmptyStateActions>
                                </EmptyStateFooter>
                              )}
                            </EmptyState>
                          </Bullseye>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </InnerScrollContainer>
            </OuterScrollContainer>
            {buildPagination('bottom')}
          </CardBody>
        </Card>
      </PageSection>

      {/* Orphans. Absent entirely when there are none, which is the normal state. */}
      {orphans.length > 0 && (
        <PageSection hasBodyWrapper isWidthLimited>
          <Card>
            <CardBody>
              <Title headingLevel="h2" size="lg">Leftover values</Title>
              <Content component="p">
                Stored values whose setting no longer exists, left behind by one that was renamed
                or removed. Nothing reads them, so they do no harm — deleting them is tidying up.
              </Content>
              <Table aria-label="Leftover parameter values" variant="compact">
                <Thead>
                  <Tr>
                    <Th>Key</Th>
                    <Th width={10}>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orphans.map(key => (
                    <Tr key={key}>
                      <Td dataLabel="Key">
                        <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}>{key}</span>
                      </Td>
                      <Td dataLabel="Actions" modifier="nowrap">
                        <RemoveButton
                          confirmationMessage={`Delete the leftover value stored for ${key}?`}
                          onRemoveConfirm={() => void onRemoveOrphan(key)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </CardBody>
          </Card>
        </PageSection>
      )}

      <ParameterHistoryModal parameter={historyOf} onClose={() => setHistoryOf(null)} />

      {/* Purpose built rather than RemoveConfirmModal: that one ends on "this action cannot be
          undone", which is not true here. A reset drops the override and can be undone by
          setting the value again, and either way the history keeps both. */}
      <Modal variant={ModalVariant.small} isOpen={!!resetting} onClose={() => setResetting(null)} aria-label="Reset setting">
        <ModalHeader title="Reset this setting?" titleIconVariant="warning" />
        <ModalBody>
          {resetting && (
            <Content component="p">
              <b>{resetting.property}</b> goes back to{' '}
              <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}>
                {valueAfterReset(resetting)}
              </span>
              {resetting.propertiesValue != null
                ? ', the value this deployment is configured with.'
                : ', the default declared in the code.'}
            </Content>
          )}
        </ModalBody>
        <ModalFooter>
          <Button key="confirm" variant="primary" onClick={() => void onResetConfirm()}>Reset</Button>
          <Button key="cancel" variant="link" onClick={() => setResetting(null)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

/**
 * Where a value could come from, for one setting. Behind the expander rather than in the row
 * because it answers a second question — "what happens if I reset this, and what did the
 * deployment ask for" — that only matters once an operator is looking at a specific setting.
 */
const ParameterDetail: React.FunctionComponent<{ parameter: Parameter }> = ({ parameter }) => (
  <DescriptionList isHorizontal isCompact columnModifier={{ default: '2Col' }}>
    <DescriptionListGroup>
      <DescriptionListTerm>Property</DescriptionListTerm>
      <DescriptionListDescription>
        <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}>{parameter.property}</span>
      </DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Type</DescriptionListTerm>
      <DescriptionListDescription>{parameter.type}</DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Default</DescriptionListTerm>
      <DescriptionListDescription>
        <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}>{parameter.defaultValue}</span>
      </DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Configured value</DescriptionListTerm>
      <DescriptionListDescription>
        {parameter.propertiesValue ?? (
          <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>
            not configured
          </span>
        )}
      </DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Config source</DescriptionListTerm>
      <DescriptionListDescription>
        {/* SmallRye names a source by its whole origin, file path and all, so this is routinely
            long enough to force the layout wide if it is not allowed to break. */}
        {parameter.configSourceName ? (
          <span style={{ wordBreak: 'break-all' }}>{parameter.configSourceName}</span>
        ) : (
          <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>none</span>
        )}
      </DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Identifier</DescriptionListTerm>
      <DescriptionListDescription>
        <Tooltip content="What this setting is stored and addressed by. Also the environment variable name, upper-cased.">
          <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}>{parameter.name}</span>
        </Tooltip>
      </DescriptionListDescription>
    </DescriptionListGroup>
  </DescriptionList>
);

export { GeneralSettings };
